import { preventOneWordInLastLine } from '@lemonade-hq/cdk';
import type { Vector } from '@lemonade-hq/ts-helpers';
import { environment } from './environment';
import { health } from './health';
import { lgbtq } from './lgbtq';
import { logo } from './logo';
import { poverty } from './poverty';
import { rights } from './rights';

import { Highlight } from '../elements';

export interface SubCategory {
  name?: string;
  notShown?: boolean;
  content: JSX.Element;
}

export interface Category {
  name: string;
  formattedName?: string;
  subCategories?: SubCategory[];
  points: [number, number][];
  desktopDotMargin?: Vector;
  mobileDotMargin?: Vector;
}

export const categories: Category[] = [
  {
    name: 'poverty',
    formattedName: 'improve global poverty and education',
    subCategories: [
      {
        content: <span>We gave others the power to pull themselves up</span>,
      },
      {
        name: 'New Story',
        content: (
          <>
            <Highlight>100 vulnerable families</Highlight>
            <span
              // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
              dangerouslySetInnerHTML={{
                __html: ` ${preventOneWordInLastLine('granted a playground and shelter in El Salvador.')}`,
              }}
            />
          </>
        ),
      },
      {
        name: 'Citymeals On Wheels',
        content: (
          <>
            <Highlight>7,469 meals</Highlight>
            <span
              // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
              dangerouslySetInnerHTML={{
                __html: ` ${preventOneWordInLastLine('delivered to homebound New Yorkers in need.')}`,
              }}
            />
          </>
        ),
      },
      {
        name: 'Habitat For Humanity',
        content: (
          <>
            <Highlight>1,500 families</Highlight>
            <span
              // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
              dangerouslySetInnerHTML={{
                __html: ` ${preventOneWordInLastLine('given safe and affordable housing throughout the U.S.')}`,
              }}
            />
          </>
        ),
      },
      {
        name: 'WIN',
        content: (
          <>
            <Highlight>205 homeless youth</Highlight>
            <span
              // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
              dangerouslySetInnerHTML={{
                __html: ` ${preventOneWordInLastLine('sponsored through college & STEM programs in NYC.')}`,
              }}
            />
          </>
        ),
      },
      {
        name: 'Feeding America',
        content: (
          <>
            <Highlight>9,707 meals</Highlight>
            <span
              // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
              dangerouslySetInnerHTML={{
                __html: ` ${preventOneWordInLastLine('distributed to local communities throughout the U.S.')}`,
              }}
            />
          </>
        ),
      },
      {
        name: 'Robin Hood',
        content: (
          <>
            <Highlight>36,000 meals</Highlight>
            <span
              // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
              dangerouslySetInnerHTML={{
                __html: ` ${preventOneWordInLastLine('provided to feed those in need at food banks throughout NYC.')}`,
              }}
            />
          </>
        ),
      },
      {
        name: 'Pencils Of Promise',
        content: (
          <>
            <Highlight>2,259 students</Highlight>
            <span
              // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
              dangerouslySetInnerHTML={{
                __html: ` ${preventOneWordInLastLine(
                  'provided with teaching materials in Ghana, Guatemala, and Laos.',
                )}`,
              }}
            />
          </>
        ),
      },
      {
        name: 'Teach For America',
        content: (
          <>
            <Highlight>3,390 teachers</Highlight>
            <span
              // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
              dangerouslySetInnerHTML={{
                __html: ` ${preventOneWordInLastLine('sponsored in high-need communities across the U.S.')}`,
              }}
            />
          </>
        ),
      },
      {
        name: 'Best Buddies',
        content: (
          <>
            <Highlight>36 one-to-one friendships</Highlight>
            <span
              // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
              dangerouslySetInnerHTML={{
                __html: ` ${preventOneWordInLastLine('formed for IDD program participants.')}`,
              }}
            />
          </>
        ),
      },
      {
        name: 'Youth Speaks',
        content: (
          <>
            <Highlight>10 sponsorships</Highlight>
            <span
              // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
              dangerouslySetInnerHTML={{
                __html: ` ${preventOneWordInLastLine(
                  'given to Black, Indigenous, Latinx, and Pacific Islander artists.',
                )}`,
              }}
            />
          </>
        ),
      },
      {
        name: 'Care',
        content: (
          <>
            <Highlight>24 emergency care kits</Highlight>
            <span
              // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
              dangerouslySetInnerHTML={{
                __html: ` ${preventOneWordInLastLine('delivered to women in Pakistan.')}`,
              }}
            />
          </>
        ),
      },
      {
        name: 'Les Restos du Coeur',
        content: (
          <>
            <Highlight>7,500 balanced meals</Highlight>
            <span
              // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
              dangerouslySetInnerHTML={{
                __html: ` ${preventOneWordInLastLine('sent to homeless shelters throughout France.')}`,
              }}
            />
          </>
        ),
      },
      {
        name: 'MEET',
        content: (
          <>
            <Highlight>46 dialogue sessions</Highlight>
            <span
              // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
              dangerouslySetInnerHTML={{
                __html: ` ${preventOneWordInLastLine('led for young Israelis and Palestinians studying together.')}`,
              }}
            />
          </>
        ),
      },
    ],
    points: poverty,
    desktopDotMargin: {
      x: -3,
      y: 48,
    },
    mobileDotMargin: {
      x: -4,
      y: 65,
    },
  },
  {
    name: 'environment',
    formattedName: 'our planet',
    subCategories: [
      {
        name: 'The Humane Society',
        notShown: true,
        content: <br />,
      },
      {
        name: 'Clean Air Task Force',
        notShown: true,
        content: <br />,
      },
      {
        name: 'Surfrider Europe',
        notShown: true,
        content: <br />,
      },
      {
        name: 'Plastic Soup Foundation',
        notShown: true,
        content: <br />,
      },
      {
        content: <span>We helped save our planet</span>,
      },
      {
        name: 'American Forests',
        content: (
          <>
            <Highlight>52,992 trees</Highlight>
            <span
              // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
              dangerouslySetInnerHTML={{
                __html: ` ${preventOneWordInLastLine(
                  'planted in critical ecosystems throughout California and Mexico.',
                )}`,
              }}
            />
          </>
        ),
      },
      {
        name: 'Alliance For The Great Lakes',
        content: (
          <>
            <Highlight>500,000 pounds of litter</Highlight>
            <span
              // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
              dangerouslySetInnerHTML={{
                __html: ` ${preventOneWordInLastLine('removed from beaches across the Great Lakes.')}`,
              }}
            />
          </>
        ),
      },
      {
        name: 'earthday.org',
        content: (
          <>
            <Highlight>7,775 trees</Highlight>
            <span
              // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
              dangerouslySetInnerHTML={{
                __html: ` ${preventOneWordInLastLine(
                  'planted in the Philippi Township, South Africa to benefit the community.',
                )}`,
              }}
            />
          </>
        ),
      },
      {
        name: 'charity: water',
        content: (
          <>
            <Highlight>12 schools</Highlight>
            <span
              // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
              dangerouslySetInnerHTML={{
                __html: ` ${preventOneWordInLastLine(
                  'granted clean, running water for 4,200 students in Bangladesh.',
                )}`,
              }}
            />
          </>
        ),
      },
      {
        name: 'The Ocean Agency',
        content: (
          <>
            <Highlight>600 nonprofits</Highlight>
            <span
              // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
              dangerouslySetInnerHTML={{
                __html: ` ${preventOneWordInLastLine(
                  'provided with free-to-use ocean imagery, to support ocean conservation.',
                )}`,
              }}
            />
          </>
        ),
      },
      {
        name: '350.org',
        content: (
          <>
            <Highlight>500 anti-fossil fuel</Highlight>
            <span
              // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
              dangerouslySetInnerHTML={{
                __html: ` ${preventOneWordInLastLine('campaigns funded in the fight against climate change.')}`,
              }}
            />
          </>
        ),
      },
      {
        name: 'SPCAI',
        content: (
          <>
            <Highlight>20,408 animals</Highlight>
            <span
              // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
              dangerouslySetInnerHTML={{
                __html: ` ${preventOneWordInLastLine('provided with food, shelter, and urgent veterinary care.')}`,
              }}
            />
          </>
        ),
      },
      {
        name: 'Trees for All',
        content: (
          <>
            <Highlight>1,200 trees</Highlight>
            <span
              // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
              dangerouslySetInnerHTML={{
                __html: ` ${preventOneWordInLastLine('planted in the Netherlands to restore biodiversity.')}`,
              }}
            />
          </>
        ),
      },
      {
        name: 'Viva con Agua',
        content: (
          <>
            <Highlight>160 school children</Highlight>
            <span
              // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
              dangerouslySetInnerHTML={{
                __html: ` ${preventOneWordInLastLine('provided with clean water in South Africa.')}`,
              }}
            />
          </>
        ),
      },
      {
        name: 'One Earth - One Ocean',
        content: (
          <>
            <Highlight>1 waste collection vessel</Highlight>
            <span
              // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
              dangerouslySetInnerHTML={{
                __html: ` ${preventOneWordInLastLine('funded to collect the plastic garbage in Kratie, Cambodia.')}`,
              }}
            />
          </>
        ),
      },
      {
        name: 'Project Rescue Ocean',
        content: (
          <>
            <Highlight>875 children</Highlight>
            <span
              // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
              dangerouslySetInnerHTML={{
                __html: ` ${preventOneWordInLastLine('in France taught about the importance of waste collection.')}`,
              }}
            />
          </>
        ),
      },
      {
        name: 'Cool Earth',
        content: (
          <>
            <Highlight>42 partnerships</Highlight>
            <span
              // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
              dangerouslySetInnerHTML={{
                __html: ` ${preventOneWordInLastLine('funded for Indigenous communities in rainforest countries.')}`,
              }}
            />
          </>
        ),
      },
      {
        name: 'Together For Animals',
        content: (
          <>
            <Highlight>25 animals</Highlight>
            <span
              // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
              dangerouslySetInnerHTML={{
                __html: ` ${preventOneWordInLastLine(
                  'given life-&#8288;saving medical treatments, aftercare, and shelter.',
                )}`,
              }}
            />
          </>
        ),
      },
      {
        name: 'World Vision',
        content: (
          <>
            <Highlight>50 people</Highlight>
            <span
              // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
              dangerouslySetInnerHTML={{
                __html: ` ${preventOneWordInLastLine(
                  'in Ghana provided with clean drinking water, and safe sanitation.',
                )}`,
              }}
            />
          </>
        ),
      },
    ],
    points: environment,
    desktopDotMargin: {
      x: -96,
      y: 262,
    },
    mobileDotMargin: {
      x: -64,
      y: 208,
    },
  },
  {
    name: 'lgbtq',
    formattedName: 'the LGBTQ+ community',
    subCategories: [
      {
        content: <span>We raised a flag for the LGBTQIA+ community</span>,
      },
      {
        name: 'The Ali Forney Center',
        content: (
          <>
            <Highlight>1 medical clinic</Highlight>
            <span
              // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
              dangerouslySetInnerHTML={{
                __html: ` ${preventOneWordInLastLine(
                  'fully funded at the nation&apos;s first drop-&#8288;in center for LGBTQIA+ youth.',
                )}`,
              }}
            />
          </>
        ),
      },
      {
        name: 'The Trevor Project',
        content: (
          <>
            <Highlight>1,191 LGBTQ</Highlight>
            <span
              // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
              dangerouslySetInnerHTML={{
                __html: ` ${preventOneWordInLastLine(
                  'young people in crisis supported with free, confidential 24/7 counseling.',
                )}`,
              }}
            />
          </>
        ),
      },
      {
        name: 'ACLU',
        content: (
          <>
            <Highlight>1 LGBTQ & HIV</Highlight>
            <span
              // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
              dangerouslySetInnerHTML={{
                __html: ` ${preventOneWordInLastLine('project backed to ensure legal protection for the community.')}`,
              }}
            />
          </>
        ),
      },
      {
        name: 'En Avant Toute(s)',
        content: (
          <>
            <Highlight>83 domestic abuse victims</Highlight>
            <span
              // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
              dangerouslySetInnerHTML={{
                __html: ` ${preventOneWordInLastLine('given support and rehabilitation.')}`,
              }}
            />
          </>
        ),
      },
    ],
    points: lgbtq,
    desktopDotMargin: {
      x: -109,
      y: 319,
    },
    mobileDotMargin: {
      x: -73,
      y: 247,
    },
  },
  {
    name: 'rights',
    formattedName: 'protect Civil Rights',
    subCategories: [
      {
        content: <span>We spoke up for the voices being silenced</span>,
      },
      {
        name: 'Schone Kleren Campagne',
        notShown: true,
        content: <br />,
      },
      {
        name: 'Reporters Without Borders',
        notShown: true,
        content: <br />,
      },
      {
        name: 'Together We Rise',
        content: (
          <>
            <Highlight>1,845 foster kids</Highlight>
            <span
              // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
              dangerouslySetInnerHTML={{
                __html: ` ${preventOneWordInLastLine('given new duffle bags, hygiene kits, and toys.')}`,
              }}
            />
          </>
        ),
      },
      {
        name: 'A21',
        content: (
          <>
            <Highlight>1 24/7 confidential helpline</Highlight>
            <span
              // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
              dangerouslySetInnerHTML={{
                __html: ` ${preventOneWordInLastLine('funded to combat human trafficking in South Africa.')}`,
              }}
            />
          </>
        ),
      },
      {
        name: 'March For Our Lives',
        content: (
          <>
            <Highlight>7 youth state advocates</Highlight>
            <span
              // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
              dangerouslySetInnerHTML={{
                __html: ` ${preventOneWordInLastLine('sponsored to fight against gun violence.')}`,
              }}
            />
          </>
        ),
      },
      {
        name: 'Thorn',
        content: (
          <>
            <Highlight>400,000 kids</Highlight>
            <span
              // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
              dangerouslySetInnerHTML={{
                __html: ` ${preventOneWordInLastLine(
                  'provided with safety resources, to protect against child trafficking.',
                )}`,
              }}
            />
          </>
        ),
      },
      {
        name: "Emma's Torch",
        content: (
          <>
            <Highlight>200 refugees</Highlight>
            <span
              // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
              dangerouslySetInnerHTML={{
                __html: ` ${preventOneWordInLastLine(
                  'provided culinary training, to find careers in the food industry.',
                )}`,
              }}
            />
          </>
        ),
      },
      {
        name: 'Code To Inspire',
        content: (
          <>
            <Highlight>63 laptops</Highlight>
            <span
              // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
              dangerouslySetInnerHTML={{
                __html: ` ${preventOneWordInLastLine(
                  'donated to girls in Afghanistan, allowing them to study virtually.',
                )}`,
              }}
            />
          </>
        ),
      },
      {
        name: 'Malala Fund',
        content: (
          <>
            <Highlight>8,000 girls</Highlight>
            <span
              // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
              dangerouslySetInnerHTML={{
                __html: ` ${preventOneWordInLastLine('provided gender equity training in Mara, Tanzania.')}`,
              }}
            />
          </>
        ),
      },
      {
        name: 'UNICEF USA',
        content: (
          <>
            <Highlight>277,667 people in need</Highlight>
            <span
              // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
              dangerouslySetInnerHTML={{
                __html: ` ${preventOneWordInLastLine('sent life-&#8288;saving water and food packs in Sudan.')}`,
              }}
            />
          </>
        ),
      },
      {
        name: 'Direct Relief',
        content: (
          <>
            <Highlight>7,000 people</Highlight>
            <span
              // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
              dangerouslySetInnerHTML={{
                __html: ` ${preventOneWordInLastLine('living in disaster zones in Ukraine delivered medical care.')}`,
              }}
            />
          </>
        ),
      },
    ],
    points: rights,
    desktopDotMargin: {
      x: 126,
      y: 183,
    },
    mobileDotMargin: {
      x: 82,
      y: 155,
    },
  },
  {
    name: 'health',
    formattedName: 'fund better healthcare',
    subCategories: [
      {
        content: <span>We distributed medical supplies to those in need</span>,
      },
      {
        name: 'The Objective Zero Foundation',
        content: (
          <>
            <Highlight>21,373 veterans</Highlight>
            <span
              // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
              dangerouslySetInnerHTML={{
                __html: ` ${preventOneWordInLastLine('provided with therapy, online support, and PTSD training.')}`,
              }}
            />
          </>
        ),
      },
      {
        name: 'Cure Childhood Cancer',
        content: (
          <>
            <Highlight>6 child cancer patients</Highlight>
            <span
              // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
              dangerouslySetInnerHTML={{
                __html: ` ${preventOneWordInLastLine('granted treatment via CURE&apos;s Precision Medicine program.')}`,
              }}
            />
          </>
        ),
      },
      {
        name: 'American Red Cross',
        content: (
          <>
            <Highlight>1,268 units of blood</Highlight>
            <span
              // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
              dangerouslySetInnerHTML={{
                __html: ` ${preventOneWordInLastLine('matched for people living with sickle cell disease.')}`,
              }}
            />
          </>
        ),
      },
      {
        name: 'National Breast Cancer Foundation',
        content: (
          <>
            <Highlight>531 mammograms</Highlight>
            <span
              // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
              dangerouslySetInnerHTML={{
                __html: ` ${preventOneWordInLastLine('administered to support early detection of breast cancer.')}`,
              }}
            />
          </>
        ),
      },
      {
        name: 'To Write Love On Her Arms',
        content: (
          <>
            <Highlight>1,118 therapy sessions</Highlight>
            <span
              // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
              dangerouslySetInnerHTML={{
                __html: ` ${preventOneWordInLastLine('sponsored for those beginning their road to recovery.')}`,
              }}
            />
          </>
        ),
      },
      {
        name: 'Hôpitaux de France',
        content: (
          <>
            <Highlight>4 hospitalized children&apos;s rooms</Highlight>
            <span
              // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
              dangerouslySetInnerHTML={{
                __html: ` ${preventOneWordInLastLine('provided with beds for family members.')}`,
              }}
            />
          </>
        ),
      },
      {
        name: 'La Ligue contre le cancer',
        content: (
          <>
            <Highlight>3 medical models</Highlight>
            <span
              // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
              dangerouslySetInnerHTML={{
                __html: ` ${preventOneWordInLastLine('funded to aid early detection of breast cancer.')}`,
              }}
            />
          </>
        ),
      },
      {
        name: 'Doctors Without Borders',
        content: (
          <>
            <Highlight>1 maternity clinic</Highlight>
            <span
              // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
              dangerouslySetInnerHTML={{
                __html: ` ${preventOneWordInLastLine('funded to support complex births in Afghanistan.')}`,
              }}
            />
          </>
        ),
      },
      {
        name: 'Kika',
        content: (
          <>
            <Highlight>1 childhood cancer research trial</Highlight>
            <span
              // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
              dangerouslySetInnerHTML={{
                __html: ` ${preventOneWordInLastLine('funded in the Netherlands to find pain-free treatments.')}`,
              }}
            />
          </>
        ),
      },
      {
        name: 'Gurls Talk',
        content: (
          <>
            <Highlight>300,000 young women</Highlight>
            <span
              // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
              dangerouslySetInnerHTML={{
                __html: ` ${preventOneWordInLastLine('provided with free mental health resources.')}`,
              }}
            />
          </>
        ),
      },
      {
        name: 'Crisis Text line',
        content: (
          <>
            <Highlight>772 emergency responses</Highlight>
            <span
              // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
              dangerouslySetInnerHTML={{
                __html: ` ${preventOneWordInLastLine('administered to those in crisis throughout the U.S.')}`,
              }}
            />
          </>
        ),
      },
    ],
    points: health,
    desktopDotMargin: {
      x: 83,
      y: 39,
    },
    mobileDotMargin: {
      x: 55,
      y: 59,
    },
  },
  {
    name: 'logo',
    points: logo,
  },
];
